import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GIGS, useApp, formatPrice, getTierPrice, getTierOriginalPrice } from '@/context/AppContext';
import { CheckoutModal } from '@/components/CheckoutModal';
import { HourlyCommitmentModal } from '@/components/HourlyCommitmentModal';
import { SEOHead } from '@/components/SEOHead';
import { ArrowLeft, Check, Clock, Code2, Shield, Zap, Users } from 'lucide-react';

export default function GigDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = useApp();
  const gig = GIGS.find((g) => g.id === id);
  const [activeTier, setActiveTier] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCommitment, setShowCommitment] = useState(false);

  if (!gig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center"><h2 className="text-2xl font-bold text-foreground mb-2">Gig Not Found</h2><Link to="/services" className="text-brand-blue font-medium">Back to Services</Link></div>
      </div>
    );
  }

  const tier = gig.tiers[activeTier];
  const price = getTierPrice(tier, currency);
  const originalPrice = getTierOriginalPrice(tier, currency);
  const deposit = Math.round(price * 0.5);

  return (
    <div className="min-h-screen bg-background" data-testid="gig-detail-page">
      <SEOHead title={`${gig.title} | MediaLabs Services`} description={gig.shortDesc} path={`/services/${gig.id}`} />
      <div className="pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link to="/services" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="back-to-services"><ArrowLeft className="w-4 h-4" /> Back to Services</Link>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10">
            {/* Left: Detail */}
            <motion.div className="lg:col-span-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">{gig.category}</span>
              <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground" data-testid="gig-detail-title">{gig.title}</h1>
              <div className="mt-6 space-y-4">
                {gig.description.split('\n\n').map((para, i) => <p key={`para-${i}-${para.slice(0,20)}`} className="text-sm sm:text-base text-muted-foreground leading-relaxed">{para}</p>)}
              </div>
              <div className="mt-8">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">Technology Stack</h3>
                <div className="flex flex-wrap gap-2" data-testid="gig-tech-stack">
                  {gig.techStack.map((tech) => (<span key={tech} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary/60 border border-[color:var(--hairline)] text-xs font-medium text-foreground"><Code2 className="w-3 h-3 text-brand-blue" /> {tech}</span>))}
                </div>
              </div>
              <div className="mt-10">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-5">Our Process</h3>
                <div className="space-y-4" data-testid="gig-process">
                  {gig.process.map((step, i) => (
                    <div key={step.step} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-9 h-9 rounded-lg bg-brand-blue/8 border border-brand-blue/15 flex items-center justify-center text-xs font-bold text-brand-blue shrink-0">{i + 1}</div>
                        {i < gig.process.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-6">
                        <span className="text-[11px] font-semibold text-brand-orange uppercase tracking-wider">{step.step}</span>
                        <h4 className="text-sm font-semibold text-foreground">{step.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Pricing */}
            <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <div className="lg:sticky lg:top-28">
                <div className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] overflow-hidden" data-testid="pricing-widget">
                  <div className="flex border-b border-[color:var(--hairline)]">
                    {gig.tiers.map((t, i) => (
                      <button key={t.name} onClick={() => setActiveTier(i)} className={`flex-1 py-3 text-xs sm:text-sm font-semibold transition-colors duration-200 ${activeTier === i ? 'text-brand-blue border-b-2 border-brand-blue bg-brand-blue/3' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tier-tab-${i}`}>{t.name}</button>
                    ))}
                  </div>
                  <div className="p-5 sm:p-6">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-extrabold text-brand-orange">{formatPrice(price, currency)}</span>
                      {tier.recurring && <span className="text-sm font-medium text-muted-foreground ml-0.5">/mo</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {tier.delivery}</span>
                      <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> {tier.recurring ? 'Managed service' : gig.isRecurring ? 'Managed service' : 'Code ownership'}</span>
                    </div>
                    <ul className="space-y-2.5 mb-6">
                      {tier.features.map((f) => (<li key={f} className="flex items-start gap-2.5"><span className="mt-0.5 w-4 h-4 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0"><Check className="w-2.5 h-2.5 text-green-600" strokeWidth={3} /></span><span className="text-sm text-foreground">{f}</span></li>))}
                    </ul>
                    <motion.button onClick={() => gig.isHourly ? setShowCommitment(true) : setShowCheckout(true)} whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }} className="pulse-orange w-full py-3.5 rounded-xl bg-brand-orange text-white text-sm font-bold shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-lift)] hover:bg-brand-orange-dark transition-shadow transition-colors duration-200" data-testid="start-project-cta">
                      {gig.isHourly ? <><Users className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Request VA / Custom Offer</> : <><Zap className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {tier.recurring ? 'Subscribe & Start' : 'Start Project & Pay Deposit'}</>}
                    </motion.button>
                    <p className="text-[11px] text-muted-foreground text-center mt-3">{tier.recurring ? 'Monthly billing \u00b7 Cancel anytime' : gig.isRecurring ? 'Monthly billing \u00b7 Cancel anytime' : '50% deposit \u00b7 Remaining on delivery'}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <CheckoutModal isOpen={showCheckout} onClose={() => setShowCheckout(false)} gig={gig} tierIndex={activeTier} />
      <HourlyCommitmentModal isOpen={showCommitment} onClose={() => setShowCommitment(false)} />
    </div>
  );
}
